import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../../lib/api.js';
import { Table, type Column } from '../../components/ui/Table.js';
import { Dialog } from '../../components/ui/Dialog.js';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { useToast } from '../../components/ui/Toast.js';
import {
  ProviderNotConfiguredCard,
  errMessage,
  isProviderNotConfigured,
  retryUnlessClientError,
  splitCsv,
} from './shared.js';
import './infrastructure.css';

interface FirewallRuleTargets {
  addresses?: string[];
  droplet_ids?: number[];
  load_balancer_uids?: string[];
  tags?: string[];
}

interface InboundRule {
  protocol: string;
  ports?: string;
  sources: FirewallRuleTargets;
}

interface OutboundRule {
  protocol: string;
  ports?: string;
  destinations: FirewallRuleTargets;
}

interface Firewall {
  id: string;
  name: string;
  status: string;
  inboundRules: InboundRule[];
  outboundRules: OutboundRule[];
  dropletIds: number[];
  tags: string[];
  createdAt: string | null;
}

const PROTOCOLS = ['tcp', 'udp', 'icmp'] as const;

interface RuleRowForm {
  protocol: string;
  ports: string;
  addresses: string;
  /** non-address targets carried over from an existing rule (droplet_ids, tags, …) */
  extraTargets: Omit<FirewallRuleTargets, 'addresses'>;
}

const DEFAULT_INBOUND: RuleRowForm = {
  protocol: 'tcp',
  ports: '22',
  addresses: '0.0.0.0/0, ::/0',
  extraTargets: {},
};

const DEFAULT_OUTBOUND: RuleRowForm = {
  protocol: 'tcp',
  ports: 'all',
  addresses: '0.0.0.0/0, ::/0',
  extraTargets: {},
};

function toRowForm(protocol: string, ports: string | undefined, targets: FirewallRuleTargets): RuleRowForm {
  const { addresses, ...extraTargets } = targets;
  return {
    protocol,
    ports: ports ?? '',
    addresses: (addresses ?? []).join(', '),
    extraTargets,
  };
}

function buildTargets(row: RuleRowForm): FirewallRuleTargets | null {
  const addresses = splitCsv(row.addresses);
  const targets: FirewallRuleTargets = {
    ...row.extraTargets,
    ...(addresses.length > 0 ? { addresses } : {}),
  };
  const hasAny =
    (targets.addresses?.length ?? 0) > 0 ||
    (targets.droplet_ids?.length ?? 0) > 0 ||
    (targets.load_balancer_uids?.length ?? 0) > 0 ||
    (targets.tags?.length ?? 0) > 0;
  return hasAny ? targets : null;
}

export function FirewallsPage() {
  const queryClient = useQueryClient();
  const toast = useToast();

  const firewallsQuery = useQuery<Firewall[]>({
    queryKey: ['do', 'firewalls'],
    queryFn: () => apiFetch<Firewall[]>('/do/firewalls'),
    retry: retryUnlessClientError,
  });

  // ── create/edit dialog ──
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Firewall | null>(null);
  const [name, setName] = useState('');
  const [inbound, setInbound] = useState<RuleRowForm[]>([{ ...DEFAULT_INBOUND }]);
  const [outbound, setOutbound] = useState<RuleRowForm[]>([{ ...DEFAULT_OUTBOUND }]);

  const openCreate = () => {
    setEditing(null);
    setName('');
    setInbound([{ ...DEFAULT_INBOUND }]);
    setOutbound([{ ...DEFAULT_OUTBOUND }]);
    setDialogOpen(true);
  };

  const openEdit = (fw: Firewall) => {
    setEditing(fw);
    setName(fw.name);
    setInbound(fw.inboundRules.map((r) => toRowForm(r.protocol, r.ports, r.sources)));
    setOutbound(fw.outboundRules.map((r) => toRowForm(r.protocol, r.ports, r.destinations)));
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditing(null);
  };

  interface FirewallBody {
    name: string;
    inbound_rules?: InboundRule[];
    outbound_rules?: OutboundRule[];
    droplet_ids?: number[];
    tags?: string[];
  }

  const buildBody = (): FirewallBody | string => {
    const trimmedName = name.trim();
    if (!trimmedName) return 'name is required';

    const inbound_rules: InboundRule[] = [];
    for (const row of inbound) {
      const ports = row.ports.trim();
      if (row.protocol !== 'icmp' && !ports) {
        return 'inbound tcp/udp rules need ports (e.g. "80", "8000-9000" or "all")';
      }
      const sources = buildTargets(row);
      if (!sources) return 'each inbound rule needs at least one source CIDR';
      inbound_rules.push({
        protocol: row.protocol,
        ...(row.protocol !== 'icmp' ? { ports } : {}),
        sources,
      });
    }

    const outbound_rules: OutboundRule[] = [];
    for (const row of outbound) {
      const ports = row.ports.trim();
      if (row.protocol !== 'icmp' && !ports) {
        return 'outbound tcp/udp rules need ports (e.g. "80", "8000-9000" or "all")';
      }
      const destinations = buildTargets(row);
      if (!destinations) return 'each outbound rule needs at least one destination CIDR';
      outbound_rules.push({
        protocol: row.protocol,
        ...(row.protocol !== 'icmp' ? { ports } : {}),
        destinations,
      });
    }

    if (inbound_rules.length === 0 && outbound_rules.length === 0) {
      return 'add at least one rule';
    }

    return {
      name: trimmedName,
      ...(inbound_rules.length > 0 ? { inbound_rules } : {}),
      ...(outbound_rules.length > 0 ? { outbound_rules } : {}),
      // PUT is a full replace — preserve attachments the dialog doesn't edit
      ...(editing && editing.dropletIds.length > 0 ? { droplet_ids: editing.dropletIds } : {}),
      ...(editing && editing.tags.length > 0 ? { tags: editing.tags } : {}),
    };
  };

  const saveMut = useMutation({
    mutationFn: ({ id, body }: { id: string | null; body: FirewallBody }) =>
      id === null
        ? apiFetch<Firewall>('/do/firewalls', { method: 'POST', body })
        : apiFetch<Firewall>(`/do/firewalls/${id}`, { method: 'PUT', body }),
    onSuccess: async (saved, vars) => {
      await queryClient.invalidateQueries({ queryKey: ['do', 'firewalls'] });
      closeDialog();
      toast.success(
        vars.id === null ? `Firewall "${saved.name}" created` : `Firewall "${saved.name}" updated`,
      );
    },
    onError: (err) => toast.error(errMessage(err, 'failed to save firewall')),
  });

  const submit = () => {
    const body = buildBody();
    if (typeof body === 'string') {
      toast.error(body);
      return;
    }
    saveMut.mutate({ id: editing?.id ?? null, body });
  };

  // ── delete ──
  const [deleteTarget, setDeleteTarget] = useState<Firewall | null>(null);
  const deleteMut = useMutation({
    mutationFn: (fw: Firewall) =>
      apiFetch<{ ok: boolean }>(`/do/firewalls/${fw.id}`, {
        method: 'DELETE',
        body: { confirmName: fw.name },
      }),
    onSuccess: async (_res, fw) => {
      await queryClient.invalidateQueries({ queryKey: ['do', 'firewalls'] });
      toast.success(`Firewall "${fw.name}" deleted`);
      setDeleteTarget(null);
    },
    onError: (err) => toast.error(errMessage(err, 'delete failed')),
  });

  const ruleEditor = (
    label: string,
    rows: RuleRowForm[],
    setRows: React.Dispatch<React.SetStateAction<RuleRowForm[]>>,
    defaultRow: RuleRowForm,
    addressesLabel: string,
  ) => (
    <div className="rule-section">
      <div className="rule-section-head">
        <span>{label}</span>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => setRows((cur) => [...cur, { ...defaultRow }])}
        >
          Add rule
        </button>
      </div>
      {rows.length === 0 ? <p className="rule-empty">No rules.</p> : null}
      {rows.map((row, i) => (
        <div key={i} className="rule-row">
          <select
            className="rule-proto"
            aria-label={`${label} protocol`}
            value={row.protocol}
            onChange={(e) =>
              setRows((cur) =>
                cur.map((r, idx) => (idx === i ? { ...r, protocol: e.target.value } : r)),
              )
            }
          >
            {PROTOCOLS.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
          <input
            className="rule-port mono"
            aria-label={`${label} ports`}
            value={row.ports}
            disabled={row.protocol === 'icmp'}
            onChange={(e) =>
              setRows((cur) => cur.map((r, idx) => (idx === i ? { ...r, ports: e.target.value } : r)))
            }
            placeholder={row.protocol === 'icmp' ? '—' : '80'}
          />
          <input
            className="rule-wide mono"
            aria-label={addressesLabel}
            value={row.addresses}
            onChange={(e) =>
              setRows((cur) =>
                cur.map((r, idx) => (idx === i ? { ...r, addresses: e.target.value } : r)),
              )
            }
            placeholder="0.0.0.0/0, ::/0"
          />
          <button
            type="button"
            className="rule-remove"
            aria-label="Remove rule"
            onClick={() => setRows((cur) => cur.filter((_, idx) => idx !== i))}
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );

  const columns: Column<Firewall>[] = [
    { key: 'name', header: 'Name', render: (fw) => <span className="infra-name">{fw.name}</span> },
    {
      key: 'droplets',
      header: 'Droplets',
      render: (fw) => <span className="ui-cell-mute">{fw.dropletIds.length}</span>,
    },
    {
      key: 'inbound',
      header: 'Inbound rules',
      render: (fw) => <span className="ui-cell-mute">{fw.inboundRules.length}</span>,
    },
    {
      key: 'outbound',
      header: 'Outbound rules',
      render: (fw) => <span className="ui-cell-mute">{fw.outboundRules.length}</span>,
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (fw) => (
        <div className="ui-cell-actions">
          <button className="btn btn-ghost btn-sm" onClick={() => openEdit(fw)}>
            Edit
          </button>
          <button className="btn btn-ghost-danger btn-sm" onClick={() => setDeleteTarget(fw)}>
            Delete
          </button>
        </div>
      ),
    },
  ];

  if (isProviderNotConfigured(firewallsQuery.error)) {
    return (
      <div>
        <div className="page-head">
          <div>
            <h1>Firewalls</h1>
            <p className="page-sub">DigitalOcean cloud firewalls.</p>
          </div>
        </div>
        <ProviderNotConfiguredCard provider="DigitalOcean" message={firewallsQuery.error.message} />
      </div>
    );
  }

  return (
    <div>
      <div className="page-head">
        <div>
          <h1>Firewalls</h1>
          <p className="page-sub">DigitalOcean cloud firewalls.</p>
        </div>
        <button className="btn btn-amber" onClick={openCreate}>
          Create firewall
        </button>
      </div>

      <Table
        columns={columns}
        rows={firewallsQuery.data ?? []}
        rowKey={(fw) => fw.id}
        empty={
          firewallsQuery.isLoading
            ? 'Loading…'
            : firewallsQuery.isError
              ? errMessage(firewallsQuery.error, 'failed to load firewalls')
              : 'No firewalls yet. Create one to get started.'
        }
      />

      {/* create / edit dialog */}
      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        title={editing ? `Edit "${editing.name}"` : 'Create firewall'}
        width={620}
      >
        <form
          className="dialog-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (!saveMut.isPending) submit();
          }}
        >
          <div className="field">
            <label htmlFor="fw-name">Name</label>
            <input
              id="fw-name"
              autoFocus
              maxLength={255}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="web-firewall"
            />
          </div>

          {ruleEditor('Inbound rules', inbound, setInbound, DEFAULT_INBOUND, 'Source CIDRs')}
          {ruleEditor('Outbound rules', outbound, setOutbound, DEFAULT_OUTBOUND, 'Destination CIDRs')}

          <p className="dialog-desc">
            Ports accept a single port ("80"), a range ("8000-9000") or "all". Sources and
            destinations are comma-separated CIDRs.
          </p>

          <div className="dialog-actions">
            <button type="button" className="btn btn-ghost" onClick={closeDialog}>
              Cancel
            </button>
            <button type="submit" className="btn btn-amber" disabled={saveMut.isPending}>
              {saveMut.isPending ? 'Saving…' : editing ? 'Save changes' : 'Create firewall'}
            </button>
          </div>
        </form>
      </Dialog>

      {/* delete confirm */}
      <ConfirmDialog
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMut.mutate(deleteTarget)}
        title="Delete firewall"
        targetName={deleteTarget?.name ?? ''}
        description="Droplets attached to this firewall lose its rules immediately."
        confirmLabel="Delete firewall"
        busy={deleteMut.isPending}
      />
    </div>
  );
}
